import React from 'react';
import { motion } from 'framer-motion';
import { personalInfo, experiences, skills, education } from '../../data/portfolioData';
import { 
  CheckSquare, 
  FileText, 
  Award,
  Briefcase,
  GraduationCap
} from 'lucide-react';

const About: React.FC = () => {
  const getSkillIcon = (skillName: string) => {
    if (skillName.toLowerCase().includes('excel') || skillName.toLowerCase().includes('word') || skillName.toLowerCase().includes('powerpoint')) {
      return <FileText size={16} />;
    } else if (skillName.toLowerCase().includes('audit') || skillName.toLowerCase().includes('analysis')) {
      return <CheckSquare size={16} />;
    } else {
      return <Award size={16} />;
    }
  };

  return (
    <section id="about" className="about">
      <div className="container">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          About Me
        </motion.h2>
        
        <div className="about-content">
          <motion.div
            className="about-text"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <p>{personalInfo.about}</p>
          </motion.div>

          {/* Experience Subsection */}
          <motion.div
            className="about-subsection"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="subsection-header">
              <Briefcase className="subsection-icon" />
              <h3>Work Experience</h3>
            </div>
            <div className="experience-grid">
              {experiences.map((exp) => (
                <div key={exp.id} className="experience-card">
                  <h4>{exp.title}</h4>
                  <p className="company">{exp.company}</p>
                  <p className="duration">{exp.duration}</p>
                  <ul>
                    {exp.description.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Skills Subsection */}
          <motion.div
            className="about-subsection"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="subsection-header">
              <Award className="subsection-icon" />
              <h3>Skills</h3>
            </div>
            <div className="skills-grid">
              <div className="skill-category">
                <h4>Technical Skills</h4>
                <ul>
                  {skills.filter(skill => skill.category === 'technical').map((skill) => (
                    <li key={skill.id}>
                      {getSkillIcon(skill.name)}
                      {skill.name}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="skill-category">
                <h4>Soft Skills</h4>
                <ul>
                  {skills.filter(skill => skill.category === 'soft').map((skill) => (
                    <li key={skill.id}>
                      {getSkillIcon(skill.name)}
                      {skill.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Education Subsection */}
          <motion.div
            className="about-subsection"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="subsection-header">
              <GraduationCap className="subsection-icon" />
              <h3>Education</h3>
            </div>
            <div className="education-grid">
              {education.map((edu) => (
                <div key={edu.id} className="education-card">
                  <h4>{edu.degree}</h4>
                  <p className="duration">{edu.duration}</p>
                  <p className="description">{edu.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
